// src/components/portal/PortalUI.js
// Shared PRESENTATIONAL primitives for the client portal design system.
// No data fetching, no business logic — styling + layout only.
import React from 'react';
import { motion } from 'framer-motion';

/* ------------------------------------------------------------------ */
/* Card — base surface used across all portal pages                    */
/* ------------------------------------------------------------------ */
export const Card = ({ className = '', children, ...rest }) => (
  <div
    className={`bg-white dark:bg-darkbg-800 rounded-xl border border-grayteck-200 dark:border-darkbg-600 shadow-card dark:shadow-card-dark ${className}`}
    {...rest}
  >
    {children}
  </div>
);

/* ------------------------------------------------------------------ */
/* StatCard — clickable KPI/filter tile                                */
/* ------------------------------------------------------------------ */
export const StatCard = ({ title, count, icon: Icon, active, onClick, delay = 0 }) => (
  <motion.button
    type="button"
    onClick={onClick}
    initial={{ opacity: 0, y: 12 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ delay }}
    whileHover={{ y: -3 }}
    whileTap={{ y: 0 }}
    aria-pressed={active}
    className={`group relative overflow-hidden rounded-xl text-left transition-shadow ${
      active
        ? 'bg-white dark:bg-darkbg-700 border border-grayteck-800 dark:border-grayteck-500 shadow-card-lg dark:shadow-card-dark'
        : 'bg-white dark:bg-darkbg-800 border border-grayteck-200 dark:border-darkbg-600 shadow-card dark:shadow-card-dark hover:shadow-card-lg'
    }`}
  >
    {/* Gold accent bar for the active tile */}
    <span
      className={`absolute left-0 top-0 h-full w-1 rounded-r bg-gold-400 transition-opacity ${
        active ? 'opacity-100' : 'opacity-0'
      }`}
    />
    <div className="p-4 sm:p-5">
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <p
            className={`text-[11px] font-bold uppercase tracking-caps mb-1.5 transition-colors truncate ${
              active
                ? 'text-grayteck-800 dark:text-grayteck-200'
                : 'text-grayteck-600 dark:text-grayteck-400'
            }`}
          >
            {title}
          </p>
          <p className="font-display text-3xl font-bold text-grayteck-900 dark:text-grayteck-100 leading-none">
            {count}
          </p>
        </div>
        {Icon && (
          <span
            className={`shrink-0 p-2.5 rounded-lg transition-colors ${
              active
                ? 'bg-grayteck-800 text-gold-400 dark:bg-grayteck-700'
                : 'bg-grayteck-100 text-grayteck-500 dark:bg-darkbg-700 dark:text-grayteck-400 group-hover:bg-grayteck-200 dark:group-hover:bg-darkbg-600'
            }`}
          >
            <Icon className="w-5 h-5" />
          </span>
        )}
      </div>
    </div>
  </motion.button>
);

/* ------------------------------------------------------------------ */
/* KpiCard — non-interactive KPI tile (read-only stats)                */
/* ------------------------------------------------------------------ */
const KPI_TONES = {
  positive:
    'bg-emerald-100 text-emerald-600 dark:bg-emerald-900/25 dark:text-emerald-400',
  attention: 'bg-gold-100 text-gold-600 dark:bg-gold-400/10 dark:text-gold-400',
  neutral:
    'bg-grayteck-100 text-grayteck-600 dark:bg-darkbg-700 dark:text-grayteck-300',
};

export const KpiCard = ({ title, value, icon: Icon, tone = 'neutral', delay = 0, className = '' }) => (
  <motion.div
    initial={{ opacity: 0, y: 12 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ delay }}
    className={`bg-white dark:bg-darkbg-800 rounded-xl border border-grayteck-200 dark:border-darkbg-600 shadow-card dark:shadow-card-dark p-4 sm:p-5 ${className}`}
  >
    <div className="flex items-center justify-between gap-3">
      <div className="min-w-0">
        <p className="text-[11px] font-bold uppercase tracking-caps text-grayteck-600 dark:text-grayteck-400 mb-1.5 truncate">
          {title}
        </p>
        <p className="font-display text-2xl sm:text-3xl font-bold text-grayteck-900 dark:text-grayteck-100 leading-none truncate">
          {value}
        </p>
      </div>
      {Icon && (
        <span className={`shrink-0 p-2.5 rounded-lg ${KPI_TONES[tone] || KPI_TONES.neutral}`}>
          <Icon className="w-5 h-5" />
        </span>
      )}
    </div>
  </motion.div>
);

/* ------------------------------------------------------------------ */
/* SectionCard — titled card section (settings/support style)          */
/* ------------------------------------------------------------------ */
export const SectionCard = ({ title, icon: Icon, action, className = '', children }) => (
  <div
    className={`bg-white dark:bg-darkbg-800 rounded-xl border border-grayteck-200 dark:border-darkbg-600 shadow-card dark:shadow-card-dark p-4 sm:p-6 mb-6 ${className}`}
  >
    <div className="flex items-center justify-between mb-5 pb-3.5 border-b border-grayteck-200 dark:border-darkbg-600">
      <div className="flex items-center gap-2.5">
        {Icon && (
          <span className="inline-flex items-center justify-center w-8 h-8 rounded-lg bg-grayteck-100 dark:bg-darkbg-700">
            <Icon className="w-4 h-4 text-grayteck-600 dark:text-grayteck-300" />
          </span>
        )}
        <h2 className="font-display text-base sm:text-lg font-bold text-grayteck-900 dark:text-grayteck-100">
          {title}
        </h2>
      </div>
      {action}
    </div>
    {children}
  </div>
);

/* ------------------------------------------------------------------ */
/* Form input class — consistent text inputs                           */
/* ------------------------------------------------------------------ */
export const inputClass =
  'w-full px-3 py-2 rounded-md text-sm border border-grayteck-300 dark:border-darkbg-500 bg-white dark:bg-darkbg-700 text-grayteck-900 dark:text-grayteck-100 placeholder:text-grayteck-400 dark:placeholder:text-grayteck-500 focus:outline-none focus:ring-2 focus:ring-grayteck-600 dark:focus:ring-gold-400 focus:border-transparent transition-all duration-200';

export const labelClass =
  'block text-xs font-bold uppercase tracking-caps text-grayteck-700 dark:text-grayteck-400 mb-1.5';

/* ------------------------------------------------------------------ */
/* StatusBadge — single source of truth for status pills               */
/* ------------------------------------------------------------------ */
const BADGE_STYLES = {
  positive:
    'bg-emerald-50 text-emerald-700 ring-1 ring-emerald-600/20 dark:bg-emerald-900/25 dark:text-emerald-300 dark:ring-emerald-400/20',
  attention:
    'bg-gold-50 text-gold-700 ring-1 ring-gold-600/30 dark:bg-gold-400/10 dark:text-gold-300 dark:ring-gold-400/25',
  negative:
    'bg-red-50 text-red-700 ring-1 ring-red-600/20 dark:bg-red-900/25 dark:text-red-300 dark:ring-red-400/20',
  neutral:
    'bg-grayteck-100 text-grayteck-700 ring-1 ring-grayteck-400/25 dark:bg-darkbg-600 dark:text-grayteck-300 dark:ring-grayteck-600/40',
};

const STATUS_TONE = {
  signed: 'positive',
  paid: 'positive',
  active: 'positive',
  trialing: 'positive',
  pending: 'attention',
  pending_signature: 'attention',
  opened: 'attention',
  past_due: 'attention',
  overdue: 'negative',
  canceled: 'neutral',
  draft: 'neutral',
  refunded: 'neutral',
};

export const statusTone = (status) => STATUS_TONE[status] || 'neutral';

export const StatusBadge = ({ status, label, tone, className = '' }) => (
  <span
    className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-bold whitespace-nowrap ${
      BADGE_STYLES[tone || statusTone(status)]
    } ${className}`}
  >
    {label ?? status}
  </span>
);

/* ------------------------------------------------------------------ */
/* Table primitives — consistent header/cell treatment                 */
/* ------------------------------------------------------------------ */
export const Th = ({ align = 'left', className = '', children }) => (
  <th
    className={`px-4 sm:px-6 py-3.5 text-${align} text-[11px] font-bold text-grayteck-600 dark:text-grayteck-400 uppercase tracking-caps ${className}`}
  >
    {children}
  </th>
);

export const theadClass = 'bg-grayteck-50 dark:bg-darkbg-700 border-b border-grayteck-200 dark:border-darkbg-600';

/* ------------------------------------------------------------------ */
/* IconButton — table row actions                                      */
/* ------------------------------------------------------------------ */
export const IconButton = ({ title, onClick, disabled, children, className = '', ...rest }) => (
  <button
    type="button"
    title={title}
    aria-label={title}
    onClick={onClick}
    disabled={disabled}
    className={`p-1.5 rounded-md text-grayteck-600 dark:text-grayteck-400 hover:text-grayteck-900 dark:hover:text-grayteck-100 hover:bg-grayteck-100 dark:hover:bg-darkbg-600 transition-colors disabled:opacity-40 disabled:pointer-events-none ${className}`}
    {...rest}
  >
    {children}
  </button>
);

/* ------------------------------------------------------------------ */
/* Buttons                                                             */
/* ------------------------------------------------------------------ */
export const PrimaryButton = ({ className = '', children, ...rest }) => (
  <button
    type="button"
    className={`inline-flex items-center justify-center gap-2 px-4 py-2 text-sm font-bold rounded-md text-accentfg bg-gold-400 hover:bg-gold-300 active:bg-gold-500 shadow-sm transition-colors disabled:opacity-50 disabled:pointer-events-none ${className}`}
    {...rest}
  >
    {children}
  </button>
);

export const SolidButton = ({ className = '', children, ...rest }) => (
  <button
    type="button"
    className={`inline-flex items-center justify-center gap-2 px-4 py-2 text-sm font-bold rounded-md text-white bg-grayteck-800 hover:bg-grayteck-900 active:bg-grayteck-950 dark:bg-grayteck-700 dark:hover:bg-grayteck-600 shadow-sm transition-colors disabled:opacity-50 disabled:pointer-events-none ${className}`}
    {...rest}
  >
    {children}
  </button>
);

export const SecondaryButton = ({ className = '', children, ...rest }) => (
  <button
    type="button"
    className={`inline-flex items-center justify-center gap-2 px-4 py-2 text-sm font-semibold rounded-md text-grayteck-700 dark:text-grayteck-200 border border-grayteck-300 dark:border-darkbg-500 bg-white dark:bg-darkbg-700 hover:bg-grayteck-100 dark:hover:bg-darkbg-600 hover:border-grayteck-400 dark:hover:border-grayteck-600 transition-colors disabled:opacity-50 disabled:pointer-events-none ${className}`}
    {...rest}
  >
    {children}
  </button>
);

/* ------------------------------------------------------------------ */
/* SearchInput — consistent search field                               */
/* ------------------------------------------------------------------ */
export const searchInputClass =
  'w-full pl-10 pr-4 py-2 rounded-lg text-sm border border-grayteck-300 dark:border-darkbg-500 bg-white dark:bg-darkbg-700 text-grayteck-900 dark:text-grayteck-100 placeholder:text-grayteck-400 dark:placeholder:text-grayteck-500 focus:outline-none focus:ring-2 focus:ring-grayteck-600 dark:focus:ring-gold-400 focus:border-transparent transition-all duration-200';

/* ------------------------------------------------------------------ */
/* PageHeader — consistent page title block                            */
/* ------------------------------------------------------------------ */
export const PageHeader = ({ title, subtitle, children }) => (
  <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-8">
    <div>
      <h1 className="font-display text-2xl sm:text-[28px] font-bold text-grayteck-900 dark:text-grayteck-100 tracking-tight">
        {title}
      </h1>
      {subtitle && (
        <p className="mt-1.5 text-sm sm:text-[15px] text-grayteck-700 dark:text-grayteck-400">
          {subtitle}
        </p>
      )}
    </div>
    {children && <div className="shrink-0">{children}</div>}
  </div>
);

/* ------------------------------------------------------------------ */
/* EmptyState — shared empty treatment                                 */
/* ------------------------------------------------------------------ */
export const EmptyState = ({ icon: Icon, title, children }) => (
  <div className="text-center py-14 px-6">
    {Icon && (
      <span className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-grayteck-100 dark:bg-darkbg-700 mb-4">
        <Icon className="w-6 h-6 text-grayteck-600 dark:text-grayteck-400" />
      </span>
    )}
    <h3 className="font-display text-base font-bold text-grayteck-900 dark:text-grayteck-100 mb-1.5">
      {title}
    </h3>
    <p className="text-sm text-grayteck-700 dark:text-grayteck-400 max-w-sm mx-auto">{children}</p>
  </div>
);

/* ------------------------------------------------------------------ */
/* Spinner — brand loading state                                       */
/* ------------------------------------------------------------------ */
export const Spinner = ({ label = 'Loading…', className = '' }) => (
  <div className={`flex flex-col items-center justify-center gap-3 ${className}`}>
    <span className="relative inline-flex w-10 h-10">
      <span className="animate-spin rounded-full w-10 h-10 border-2 border-grayteck-300 dark:border-darkbg-500 border-t-grayteck-700 dark:border-t-gold-400" />
    </span>
    <span className="text-sm text-grayteck-600 dark:text-grayteck-400">{label}</span>
  </div>
);
